import React, { useEffect, useState, useCallback } from "react";
import { Icon } from "@iconify/react";
import api from "../services/api";
import PageHeader from "../components/common/PageHeader";
import { showToast } from "../helper/notifications";

const GoogleConnectionsPage = () => {
  const [gbpData, setGbpData] = useState({
    isCurrentUserConnected: false,
    totalGbpLocations: 0,
    oauthConnections: [],
    accounts: [],
  });
  const [companyAccountId, setCompanyAccountId] = useState("");
  const [loading, setLoading] = useState(true);
  const [fetchingAccounts, setFetchingAccounts] = useState(false);
  const [importingAccountId, setImportingAccountId] = useState(null);

  const fetchConnectionsData = useCallback(async () => {
    setLoading(true);
    try {
      const [gbpRes, companyRes] = await Promise.all([
        api.get("/company/gbp-connections"),
        api.get("/company").catch(() => ({ data: null })),
      ]);

      if (gbpRes.data?.success) {
        setGbpData({
          isCurrentUserConnected: Boolean(gbpRes.data.isCurrentUserConnected),
          totalGbpLocations: Number(gbpRes.data.totalGbpLocations || 0),
          oauthConnections: gbpRes.data.oauthConnections || [],
          accounts: gbpRes.data.accounts || [],
        });
      }

      if (companyRes?.data?.google_account_id) {
        setCompanyAccountId(companyRes.data.google_account_id);
      }
    } catch (err) {
      console.error(err);
      showToast("error", "Failed to load Google Business Profile connections");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnectionsData();
  }, [fetchConnectionsData]);

  const handleConnectGoogle = async () => {
    try {
      const res = await api.get("/google-business/connect-url");
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        showToast("error", "Could not generate Google authorization URL");
      }
    } catch (err) {
      console.error(err);
      showToast("error", err.response?.data?.error || "Failed to initiate Google connection");
    }
  };

  const handleRefreshAccounts = async () => {
    setFetchingAccounts(true);
    try {
      await api.post("/google-business/fetch-accounts");
      showToast("success", "Google Business accounts refreshed from Google API");
      await fetchConnectionsData();
    } catch (err) {
      console.error(err);
      showToast("error", err.response?.data?.error || "Failed to refresh Google accounts");
    } finally {
      setFetchingAccounts(false);
    }
  };

  const handleImportAccountLocations = async (account) => {
    if (!account?.account_id) return;
    setImportingAccountId(account.account_id);
    try {
      const res = await api.post("/google-business/import-locations", {
        accountId: account.account_id,
        userId: account.user_id,
      });
      const stats = res.data?.data;
      if (stats) {
        showToast(
          "success",
          `Imported ${stats.total ?? 0} profiles (${stats.inserted ?? 0} new, ${stats.updated ?? 0} updated, ${stats.unchanged ?? 0} unchanged)`
        );
      } else {
        showToast("success", res.data?.message || "Business profiles imported successfully");
      }
      await fetchConnectionsData();
    } catch (err) {
      console.error(err);
      showToast("error", err.response?.data?.error || "Failed to import profiles from Google");
    } finally {
      setImportingAccountId(null);
    }
  };

  const formatDateTime = (dt) => {
    if (!dt) return "—";
    try {
      return new Date(dt).toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return String(dt);
    }
  };

  return (
    <>
      <PageHeader
        title="Google Connections"
        subtitle="Manage Google Business Profile OAuth connections and location imports."
        actions={
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              onClick={handleRefreshAccounts}
              disabled={fetchingAccounts || loading}
              className="btn btn-outline-secondary radius-8 px-16 py-8 d-flex align-items-center gap-2 text-xs fw-semibold"
            >
              <Icon
                icon={fetchingAccounts ? "line-md:loading-twotone-loop" : "solar:refresh-linear"}
                className="text-base"
              />
              {fetchingAccounts ? "Syncing Accounts..." : "Fetch Accounts from Google"}
            </button>
            <button
              type="button"
              onClick={handleConnectGoogle}
              className="btn btn-primary-600 radius-8 px-20 py-8 d-flex align-items-center gap-2 text-xs fw-semibold"
            >
              <Icon icon="solar:link-circle-linear" className="text-base" />
              {gbpData.isCurrentUserConnected
                ? "Reconnect Google Account"
                : "Connect Google Account"}
            </button>
          </div>
        }
      />

      {loading ? (
        <div className="card radius-12 p-40 text-center text-secondary-light text-sm">
          Loading Google Business Profile connections...
        </div>
      ) : (
        <div className="row gy-4">
          {/* 1. Established OAuth User Connections */}
          <div className="col-12">
            <div className="card radius-12">
              <div className="card-header border-bottom bg-base py-14 px-24 d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <Icon icon="logos:google-icon" className="text-base" />
                  <h6 className="text-sm fw-semibold mb-0">
                    Established OAuth User Connections
                  </h6>
                  <span className="badge bg-neutral-100 text-primary-light border px-10 py-4 radius-4 text-xs fw-medium ms-2">
                    {gbpData.oauthConnections.length}{" "}
                    {gbpData.oauthConnections.length === 1 ? "Connection" : "Connections"}
                  </span>
                </div>
              </div>

              <div className="card-body p-24">
                {gbpData.oauthConnections.length === 0 ? (
                  <div className="border radius-8 p-16 text-secondary-light text-xs d-flex align-items-center justify-content-between">
                    <span>No Google OAuth connection established yet.</span>
                    <button
                      type="button"
                      onClick={handleConnectGoogle}
                      className="btn btn-primary-600 radius-8 px-12 py-4 text-xs fw-semibold"
                    >
                      Connect Now
                    </button>
                  </div>
                ) : (
                  <div className="row g-3">
                    {gbpData.oauthConnections.map((conn) => {
                      const isExpired =
                        conn.expiry_date && Number(conn.expiry_date) < Date.now();
                      const hasRefresh = Boolean(conn.has_refresh_token);
                      return (
                        <div className="col-md-6" key={conn.user_id}>
                          <div className="border radius-8 p-16 d-flex align-items-center justify-content-between gap-3 bg-base">
                            <div className="d-flex align-items-center gap-12">
                              {conn.user_picture ? (
                                <img
                                  src={conn.user_picture}
                                  alt={conn.user_name || "User"}
                                  className="rounded-circle object-fit-cover"
                                  style={{ width: 38, height: 38 }}
                                />
                              ) : (
                                <div
                                  className="rounded-circle bg-neutral-200 d-flex align-items-center justify-content-center fw-semibold text-xs"
                                  style={{ width: 38, height: 38 }}
                                >
                                  {(conn.user_name || "U").charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div>
                                <div className="d-flex align-items-center gap-2">
                                  <span className="text-sm fw-semibold text-primary-light">
                                    {conn.user_name || `User #${conn.user_id}`}
                                  </span>
                                  {conn.user_role && (
                                    <span className="badge bg-neutral-100 text-secondary-light border px-6 py-2 radius-4 text-2xs">
                                      {conn.user_role}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-secondary-light">
                                  {conn.user_email || "No email"}
                                </div>
                                <div className="text-2xs text-secondary-light mt-2">
                                  Last Token Sync: {formatDateTime(conn.updated_at)}
                                </div>
                              </div>
                            </div>

                            <div className="d-flex flex-column align-items-end gap-1">
                              <span
                                className={`badge px-10 py-4 radius-4 text-xs fw-semibold ${
                                  hasRefresh || !isExpired
                                    ? "bg-success-focus text-success-main border border-success-main"
                                    : "bg-warning-focus text-warning-main border border-warning-main"
                                }`}
                              >
                                {hasRefresh
                                  ? "Connected (Auto-Refresh)"
                                  : isExpired
                                  ? "Token Expired"
                                  : "Connected"}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 2. Connected Google Business Accounts / Location Groups & Import */}
          <div className="col-12">
            <div className="card radius-12">
              <div className="card-header border-bottom bg-base py-14 px-24 d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <Icon icon="solar:buildings-3-linear" className="text-lg" />
                  <h6 className="text-sm fw-semibold mb-0">
                    Google Business Accounts &amp; Location Groups (Import)
                  </h6>
                  <span className="badge bg-neutral-100 text-primary-light border px-10 py-4 radius-4 text-xs fw-medium ms-2">
                    {gbpData.accounts.length}{" "}
                    {gbpData.accounts.length === 1 ? "Account Group" : "Account Groups"}
                  </span>
                  <span className="badge bg-neutral-100 text-primary-light border px-10 py-4 radius-4 text-xs fw-medium">
                    {gbpData.totalGbpLocations} Imported Profiles
                  </span>
                </div>
              </div>

              <div className="card-body p-24">
                {gbpData.accounts.length === 0 ? (
                  <div className="border radius-8 p-16 text-secondary-light text-xs">
                    No Google Business accounts or location groups found. Click{" "}
                    <strong>Fetch Accounts from Google</strong> to load available accounts.
                  </div>
                ) : (
                  <div className="table-responsive border radius-8">
                    <table className="table bordered-table mb-0 align-middle">
                      <thead>
                        <tr>
                          <th className="text-xs">Account / Location Group</th>
                          <th className="text-xs">Google Account ID</th>
                          <th className="text-xs">Type</th>
                          <th className="text-xs">Connected Via</th>
                          <th className="text-xs text-center">Imported Profiles</th>
                          <th className="text-xs">Last Import / Sync</th>
                          <th className="text-xs text-end">Import Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gbpData.accounts.map((acc) => {
                          const isPrimaryCompanyAccount =
                            companyAccountId &&
                            companyAccountId.trim() === acc.account_id;
                          const isImporting = importingAccountId === acc.account_id;

                          return (
                            <tr key={acc.id}>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <span className="text-xs fw-semibold text-primary-light">
                                    {acc.account_name}
                                  </span>
                                  {isPrimaryCompanyAccount && (
                                    <span className="badge bg-primary-600 text-white px-8 py-2 radius-4 text-2xs fw-medium">
                                      Company Account
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <code className="text-xs text-secondary-light">
                                  {acc.account_id}
                                </code>
                              </td>
                              <td>
                                <span className="badge bg-neutral-100 text-primary-light border px-8 py-3 radius-4 text-2xs fw-medium">
                                  {acc.type || "ACCOUNT"}
                                </span>
                              </td>
                              <td>
                                <div className="text-xs fw-medium text-primary-light">
                                  {acc.user_name || `User #${acc.user_id}`}
                                </div>
                                {acc.user_email && (
                                  <div className="text-2xs text-secondary-light">
                                    {acc.user_email}
                                  </div>
                                )}
                              </td>
                              <td className="text-center">
                                <span
                                  className={`badge px-10 py-4 radius-4 text-xs fw-semibold ${
                                    Number(acc.imported_locations_count) > 0
                                      ? "bg-neutral-900 text-white"
                                      : "bg-neutral-100 text-secondary-light border"
                                  }`}
                                >
                                  {acc.imported_locations_count || 0}
                                </span>
                              </td>
                              <td className="text-xs text-secondary-light">
                                {formatDateTime(acc.last_synced_at)}
                              </td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  onClick={() => handleImportAccountLocations(acc)}
                                  disabled={Boolean(importingAccountId)}
                                  className="btn btn-primary-600 radius-8 px-12 py-6 d-inline-flex align-items-center gap-1 text-xs fw-semibold"
                                >
                                  <Icon
                                    icon={
                                      isImporting
                                        ? "line-md:loading-twotone-loop"
                                        : "solar:download-minimalistic-linear"
                                    }
                                    className="text-sm"
                                  />
                                  {isImporting ? "Importing..." : "Import Profiles"}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GoogleConnectionsPage;

